import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@material-ui/core';
import { Info as InfoIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineHtml from '../../../common/inline/InlineHtml';
import InlineFormik from '../../../common/inline/InlineFormik';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useLinkTransform } from '../../../common/HeaderBackButton';
import InlineSelect from '../../../common/inline/InlineSelect';
import { useUpdateTask } from '../../tasks/Task.hooks';
import ProductRiskSelector from '../components/ProductRiskSelector';
import { useUpdateProductDesignInput } from '../Product.hooks';
import {
  ProductDesignInput,
  BaseProductDesignInputCardProps
} from '../Product.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { getProductEntityName } from '../Product.config';
import environment from '../../../environments';

const DesignInputInfoCard: React.FC<BaseProductDesignInputCardProps> = (
  props: BaseProductDesignInputCardProps
) => {
  const {
    title = `Product ${getProductEntityName('designInput')} Info`,
    productId,
    productDesignInput,
    productDesignInputVersion,
    hidden = false,
    enableTitleRedirect = false,
    onChange,
    onUpdate,
    onError
  } = props;
  const initialData = productDesignInputVersion || productDesignInput;
  const initialValues: Partial<ProductDesignInput> = {
    productId,
    title: initialData.title || '',
    essentialRequirements: initialData.essentialRequirements || '',
    description: initialData.description || '',
    verificationMethod: initialData.verificationMethod || '',
    whenToVerify: initialData.whenToVerify || '',
    risk: initialData.risk
  };
  const getPath = useLinkTransform(
    `/portal/products/${productId}/design-inputs/${productDesignInput.id}`,
    [{ query: 'from' }]
  );
  const {
    updatedProductDesignInput,
    isUpdatingProductDesignInput: isUpdating,
    updateProductDesignInputError: updateError,
    updateProductDesignInput
  } = useUpdateProductDesignInput(productId, productDesignInput.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(productDesignInput.task.id || 0);
  const error: FetchError | undefined = updateError || updateTaskError;
  const { isFieldDisabled } = useTaskEntityFields(productDesignInput.task);

  useEffect(() => {
    if (updatedProductDesignInput) {
      onChange?.(updatedProductDesignInput);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProductDesignInput]);

  useEffect(() => {
    if (updatedTask) {
      productDesignInput.task = Object.assign(
        productDesignInput.task || {},
        updatedTask
      );
      onChange?.(productDesignInput);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Card style={hidden ? { display: 'none' } : undefined}>
      <CardTitle>
        <InfoIcon />
        {enableTitleRedirect ? (
          <Link className='link' to={getPath()}>
            {title}
          </Link>
        ) : (
          title
        )}
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          error={updateError}
          isSubmitting={isUpdating}
          initialValues={initialValues}
          onSubmit={(values: Partial<ProductDesignInput>) => {
            updateProductDesignInput(values);
            onChange?.(values);
          }}
        >
          <InlineForm>
            <InlineTextField
              disabled
              name='productId'
              label='Product ID'
              value={`PRODUCT-${productId}`}
            />
            <InlineTextField
              name='title'
              label='Title'
              placeholder={`Enter title for Product ${getProductEntityName(
                'designInput'
              )}`}
              disabled={isUpdating || isFieldDisabled('title')}
            />
            {!environment.productConfig?.designInput?.hideRiskField && (
              <ProductRiskSelector
                name='risk'
                label='Risk'
                placeholder='Set the Risk value'
                disabled={isUpdating || isFieldDisabled('risk')}
              />
            )}
            {environment.productConfig?.designInput?.useCustomInfoFields && (
              <InlineSelect
                name='essentialRequirements'
                label='Essential Requirements (Y/N)'
                disabled={
                  isUpdating || isFieldDisabled('essentialRequirements')
                }
                options={[
                  { value: 'yes', label: 'Y' },
                  { value: 'no', label: 'N' }
                ]}
              />
            )}
            <InlineHtml
              name='description'
              label='Description'
              helperText={`Enter description for Product ${getProductEntityName(
                'designInput'
              )}`}
              disabled={isUpdating || isFieldDisabled('description')}
              attachments={productDesignInput.task.attachments || []}
              onChangeAttachments={(attachments: InlineFileValue[]) => {
                updateTask({ attachments });
                if (productDesignInput.task) {
                  productDesignInput.task.attachments = attachments;
                }
                onChange?.(productDesignInput);
              }}
            />
            {environment.productConfig?.designInput?.useCustomInfoFields && (
              <>
                <InlineTextField
                  name='verificationMethod'
                  label='Verification Method'
                  disabled={isUpdating || isFieldDisabled('verificationMethod')}
                />
                <InlineTextField
                  name='whenToVerify'
                  label='When to Verify'
                  disabled={isUpdating || isFieldDisabled('whenToVerify')}
                />
              </>
            )}
          </InlineForm>
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default DesignInputInfoCard;
